import React from 'react'
import {
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { useTranslation } from 'react-i18next'
import { Task } from '../../types'
import { getTaskTypeLabel, formatTaskDate } from './taskTypeLabel'

interface TaskGroupedTableProps {
  tasks: Task[]
  getAssigneeLabel: (task: Task) => string
  getYardLabel: (task: Task) => string
  onEdit: (task: Task) => void
  onDelete: (task: Task) => void
  onMarkDone: (task: Task) => void
}

const STATUS_COLOR: Record<string, 'info' | 'warning' | 'success'> = {
  open: 'info',
  in_progress: 'warning',
  done: 'success',
}

function TaskGroupedTable({ tasks, getAssigneeLabel, getYardLabel, onEdit, onDelete, onMarkDone }: TaskGroupedTableProps): React.JSX.Element {
  const { t, i18n } = useTranslation()

  // Group tasks by assignee label
  const grouped = tasks.reduce<Record<string, Task[]>>((acc, task) => {
    const label = getAssigneeLabel(task)
    if (!acc[label]) acc[label] = []
    acc[label].push(task)
    return acc
  }, {})

  const groupKeys = Object.keys(grouped).sort()

  return (
    <Box mt={2}>
      {groupKeys.length === 0 ? (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
          {t('tasks.noTasks')}
        </Typography>
      ) : (
        groupKeys.map(groupLabel => (
          <Box key={groupLabel} mb={3}>
            <Typography variant="subtitle1" fontWeight={700} mb={1}>
              {groupLabel}
              <Chip label={grouped[groupLabel].length} size="small" sx={{ ml: 1, height: 20, fontSize: 11 }} />
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('tasks.fields.title')}</TableCell>
                    <TableCell>{t('tasks.fields.dueDate')}</TableCell>
                    <TableCell>{t('users.status')}</TableCell>
                    <TableCell>{t('form.label.type')}</TableCell>
                    <TableCell>{t('yard.title')}</TableCell>
                    <TableCell align="right">{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {grouped[groupLabel].map(task => (
                    <TableRow key={task.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{task.title}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatTaskDate(task.dueDate, i18n?.language, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={t(`tasks.status.${task.status}`)}
                          size="small"
                          color={STATUS_COLOR[task.status] || 'default'}
                          sx={{ fontSize: 11, height: 22 }}
                        />
                      </TableCell>
                      <TableCell>{getTaskTypeLabel(t, task.type)}</TableCell>
                      <TableCell>{getYardLabel(task)}</TableCell>
                      <TableCell align="right">
                        {task.status !== 'done' && (
                          <Tooltip title={t('tasks.markDone')}>
                            <IconButton size="small" color="success" onClick={() => onMarkDone(task)}>
                              <CheckCircleOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={t('tasks.edit')}>
                          <IconButton size="small" color="primary" onClick={() => onEdit(task)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('tasks.delete')}>
                          <IconButton size="small" color="error" onClick={() => onDelete(task)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))
      )}
    </Box>
  )
}

export default TaskGroupedTable
